import { useEffect, useRef } from "react";
import brandLockup from "../assets/images/icons/brand-lockup.svg";
import { RoomId, useTranslation } from "../components/Localization";
import { getRecordedRoomAudioSources, getRoomAudioResult } from "../services/audioListening";
import { getGameScore } from "../services/gameScores";
import { submitTotalPoints } from "../services/pointsApi";

const scoredRooms: RoomId[] = ["residence", "dining", "mirror", "salon", "estate", "legacy", "war"];
const COMPLETION_BONUS = 3;
const MAXIMUM_TOTAL_SCORE = 200;

export default function CompletionPage() {
  const { room, t } = useTranslation();
  const roomResults = scoredRooms.map((roomId) => {
    const translatedRoom = room(roomId);
    const sources = getRecordedRoomAudioSources(roomId);
    return { roomId, name: translatedRoom?.name || roomId, ...getRoomAudioResult(roomId, sources) };
  });
  const gameResults = [
    { key: "matching", label: t("completion.games.matching"), score: getGameScore("matching"), max: 15 },
    { key: "restoration", label: t("completion.games.restoration"), score: getGameScore("restorationOne") + getGameScore("restorationTwo"), max: 20 },
    { key: "painting", label: t("completion.games.painting"), score: getGameScore("paintingOne") + getGameScore("paintingTwo"), max: 20 },
    { key: "rings", label: t("completion.games.rings"), score: getGameScore("rings"), max: 15 },
    { key: "escape", label: t("completion.games.escape"), score: getGameScore("escape"), max: 15 },
    { key: "completion", label: t("completion.games.completion"), score: COMPLETION_BONUS, max: COMPLETION_BONUS },
  ];
  const totalScore = roomResults.reduce((sum, result) => sum + result.score, 0)
    + gameResults.reduce((sum, result) => sum + result.score, 0);
  const scoreDataReady = scoredRooms.every((roomId) => Boolean(room(roomId)));
  const submittedScoreRef = useRef<number | null>(null);

  useEffect(() => {
    if (!scoreDataReady || submittedScoreRef.current === totalScore) return;
    submittedScoreRef.current = totalScore;
    submitTotalPoints(totalScore).catch((error) => {
      submittedScoreRef.current = null;
      console.warn("The total score could not be submitted.", error);
    });
  }, [totalScore, scoreDataReady]);

  return <main className="screen completion-page" aria-label={t("completion.label")}>
    <img className="completion-brand" src={brandLockup} alt={t("accessibility.brand")} />
    <div className="completion-title"><h1>{t("common.completed")}</h1></div>
    <section className="completion-score-card">
      <strong className="completion-total">{t("completion.totalScore", { score: totalScore, max: MAXIMUM_TOTAL_SCORE })}</strong>
      <div className="completion-score-section">
        <h2>{t("completion.roomsTitle")}</h2>
        <ul>{roomResults.map((result) => <li key={result.roomId}>
          <div><span>{result.name}</span><small>{t("completion.audioTracks", { completed: result.completedTracks, total: result.totalTracks })}</small></div>
          <strong>{result.score}/{result.maxScore}</strong>
        </li>)}</ul>
      </div>
      <div className="completion-score-section">
        <h2>{t("completion.gamesTitle")}</h2>
        <ul>{gameResults.map((result) => <li key={result.key}><span>{result.label}</span><strong>{result.score}/{result.max}</strong></li>)}</ul>
      </div>
    </section>
  </main>;
}
